import { PIPELINE_STAGES, REVIEW_STAGES, UNIT_LABEL, pipelineCounts, taskDisplayStage } from '@/lib/stages'
import type { PipelineIntake, PipelineTask, StageCount } from '@/lib/stages'
import type { Task } from '@/lib/types'

/** An agent stage's number: sessions at work, then — spelled out — what waits on a person. */
export function AgentCount({ count }: { count: StageCount }) {
  return <>
    <span className="num">{count.running + count.queued}</span>
    {count.needsExpert > 0 && <span className="num-note">{count.needsExpert} waiting for an expert</span>}
    {count.needsDeveloper > 0 && <span className="num-note warn">{count.needsDeveloper} need{count.needsDeveloper === 1 ? 's' : ''} a developer</span>}
  </>
}

export const agentCountLabel = (label: string, count: StageCount) =>
  `${label}: ${count.running} running, ${count.queued} queued, ${count.needsExpert} waiting for an expert, ${count.needsDeveloper} need a developer`


/**
 * The six-stage board (AGENTS.md). Templates and workflow drafts are counted in stages 1–3 and
 * workflows in stages 4–6, so both lists are passed and nothing is counted twice. The two agent
 * stages show their running sessions and, separately, the sessions that need a developer.
 *
 * `scientific={false}` keeps the task flow's own stage strip (`REVIEW_STAGES`), which is a
 * different pipeline: one task, stored stages, no templates.
 */
export function TaskPipeline({ tasks, selected, onSelect, scientific = false, intakes }: { tasks: PipelineTask[]; selected: string; onSelect: (stage: string) => void; scientific?: boolean; intakes?: PipelineIntake[] }) {
  if (scientific) {
    const counts = pipelineCounts(tasks, intakes ?? [])
    return <nav className="flow expert-pipeline scientific-pipeline" aria-label="Workflows by stage">{PIPELINE_STAGES.map(stage => {
      const count = counts[stage.key]
      const label = `${stage.num}. ${stage.label}`
      return <button type="button" key={stage.key} className={`owner-${stage.owner}${selected === stage.key ? ' selected' : ''}`}
        aria-pressed={selected === stage.key} onClick={() => onSelect(selected === stage.key ? '' : stage.key)}
        title={`Counts ${UNIT_LABEL[stage.unit]}`}
        aria-label={stage.agent ? agentCountLabel(label, count) : `${label}: ${count.total}`}>
        <span className="lbl">{label}</span><span className="who">{stage.owner}</span>
        {stage.agent ? <AgentCount count={count} /> : <span className="num">{count.total}</span>}
      </button>
    })}</nav>
  }
  const counts = new Map<number, number>()
  for (const task of tasks) {
    const stage = taskDisplayStage(task as Task)
    counts.set(stage, (counts.get(stage) ?? 0) + 1)
  }
  return <nav className="flow expert-pipeline" data-tour="task-pipeline" aria-label="Tasks by stage">{REVIEW_STAGES.map(s => <button type="button" key={s.stage}
    className={`owner-${s.owner ?? 'none'}${selected === String(s.stage) ? ' selected' : ''}`} aria-pressed={selected === String(s.stage)}
    onClick={() => onSelect(selected === String(s.stage) ? '' : String(s.stage))}>
    <span className="lbl">{s.num}. {s.label}</span><span className="who">{s.owner === 'sme' ? 'expert' : s.owner === 'engineer' ? 'developer' : s.owner === 'agent' ? 'agent' : 'complete'}</span>
    <span className="num">{counts.get(s.stage) ?? 0}</span>
  </button>)}</nav>
}
